import { CodeIcon, ExternalLinkIcon } from "@heroicons/react/solid";
import React from 'react';
import { projects } from "../data";

function Projects() {
    return (
        <section id="projects" className="relative py-12 sm:py-16 lg:py-20">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>
            
            <div className="relative container mx-auto px-4 sm:px-6 lg:px-12">
                {/* Header Section */}
                <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-2xl mb-6 shadow-lg">
                        <CodeIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-5xl font-bold text-white mb-4">
                        <span className="text-blue-500">Featured Projects</span>
                    </h2>
                    <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                        Explore my latest work showcasing modern web development, innovative solutions, and creative problem-solving.
                    </p>
                    <div className="w-24 h-1 bg-blue-500 mx-auto mt-6 rounded-full"></div>
                </div>

                {/* Projects Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
                    {projects.map((project, index) => (
                        <div
                            key={project.image}
                            className="group relative bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-2"
                            style={{
                                animationDelay: `${index * 150}ms`,
                                animation: 'fadeInUp 0.8s ease-out forwards'
                            }}
                        >
                            {/* Project Image/Video */}
                            <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                                {project.image.endsWith('.mp4') || project.image.endsWith('.webm') || project.image.endsWith('.mov') ? (
                                    <video
                                        src={project.image}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        aria-label={project.title}
                                    />
                                ) : (
                                    <img
                                        src={project.image}
                                        alt={project.title}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60"></div>
                                
                                {/* Overlay on hover */}
                                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <a
                                        href={project.link}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        className="inline-flex items-center px-6 py-3 bg-blue-500 text-white font-semibold rounded-full hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                                    >
                                        <span>View Project</span>
                                        <ExternalLinkIcon className="w-4 h-4 ml-2" />
                                    </a>
                                </div>
                            </div>

                            {/* Project Content */}
                            <div className="p-6">
                                <div className="flex items-center gap-2 mb-3">
                                    {project.subtitle.split(', ').map((tech, techIndex) => (
                                        <span
                                            key={techIndex}
                                            className="px-3 py-1 text-xs font-medium bg-blue-500/20 text-blue-300 rounded-full border border-blue-500/30"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                                
                                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-300">
                                    {project.title}
                                </h3>
                                
                                <p className="text-gray-300 leading-relaxed mb-4">
                                    {project.description}
                                </p>
                                
                                <a
                                    href={project.link}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium transition-colors duration-300 group/link"
                                >
                                    <span>Learn more</span>
                                    <ExternalLinkIcon className="w-4 h-4 ml-1 transition-transform duration-300 group-hover/link:translate-x-1" />
                                </a>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Call to Action */}
                <div className="text-center mt-16">
                    <p className="text-gray-300 mb-6">
                        Interested in working together?
                    </p>
                    <a
                        href="#contact"
                        className="inline-flex items-center px-8 py-4 bg-blue-500 text-white font-semibold rounded-full hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                        Get In Touch
                    </a>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </section>
    );
}

export default Projects



// WEBPACK FOOTER //
// ./src/components/Projects.js